import { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';

import URLS from 'jsx/constants/urls';
import HTTP_STATUS_CODES from 'jsx/constants/httpStatusCodes';
import cookie from 'jsx/utils/cookie';

import { AxiosBuilderInterceptor } from './types';

const XSRF_TOKEN_COOKIE_NAME = 'XSRF-TOKEN';
const RETRY_FLAG = '_csrfRetry';

// Shared promise so concurrent 419s only trigger one token refresh
let refreshPromise: Promise<void> | null = null;

const refreshCsrfToken = (axios: AxiosInstance): Promise<void> => {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      cookie.remove(XSRF_TOKEN_COOKIE_NAME);
      await axios.get(URLS.api.csrf());
    })().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

/**
 * A response interceptor that detects 419 (CSRF token mismatch) errors,
 * refreshes the XSRF token, and retries the original request once.
 *
 * This handles the case where the XSRF-TOKEN cookie persists in the browser
 * but the server-side session (and its CSRF token) has been evicted from Redis.
 */
export const csrfRetryHandler = (
  axios: AxiosInstance,
): AxiosBuilderInterceptor => ({
  type: 'response',
  errorHandler: async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & {
      [RETRY_FLAG]?: boolean;
    };

    if (
      error.response?.status === HTTP_STATUS_CODES.csrfMismatch &&
      originalRequest &&
      !originalRequest[RETRY_FLAG]
    ) {
      originalRequest[RETRY_FLAG] = true;

      try {
        await refreshCsrfToken(axios);
      } catch {
        // If the refresh fails (network error, 500, etc.), re-throw the
        // original 419 so downstream interceptors see a consistent error.
        return Promise.reject(error);
      }

      // Retry the original request — axios reads the new cookie automatically
      return axios(originalRequest);
    }

    return Promise.reject(error);
  },
});
